<template>
  <div class="platform-guide-page container-fluid py-4">
    <!-- Breadcrumb / Header (Matching Debugger Design) -->
    <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb mb-0 align-items-center gap-2" style="font-size: 24px; line-height: 24px; font-weight: 900;">
          <li class="breadcrumb-item d-flex align-items-center" style="color: #435666;" aria-current="page">
            <i class="fa-solid fa-book-open text-info me-2"></i>
            <span>Platform Guide</span>
          </li>
          <li v-if="latestVersion" class="d-flex align-items-center">
            <span class="version-pill-highlight-sm font-monospace">
              <i class="fa-solid fa-tag me-1 opacity-75"></i>
              <span>Current: {{ latestVersion }}</span>
            </span>
          </li>
        </ol>
      </nav>

      <!-- Actions for SuperAdmins & Team -->
      <div class="d-flex align-items-center gap-2">
        <router-link 
          to="/platform-releases" 
          class="btn btn-header-secondary fw-bold d-flex align-items-center gap-2 px-3 py-2 text-sm"
        >
          <i class="fa-solid fa-layer-group"></i>
          <span>Multi-Platform Releases Hub</span>
        </router-link>
      </div>
    </div>

    <!-- Top-Level Tabs (Identical Tab Design to Debugger) -->
    <ul class="nav nav-tabs mb-4" id="mainGuideTabs" role="tablist">
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link px-4 py-3 fw-bold" 
          :class="{ active: activeTab === 'changelogs' }" 
          @click="activeTab = 'changelogs'" 
          type="button"
        >
          <i class="fa-solid fa-clock-rotate-left me-2"></i>Changelogs & Releases
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link px-4 py-3 fw-bold" 
          :class="{ active: activeTab === 'modules' }" 
          @click="activeTab = 'modules'" 
          type="button"
        >
          <i class="fa-solid fa-map-location-dot me-2"></i>Module Workflows & Guides
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button 
          class="nav-link px-4 py-3 fw-bold" 
          :class="{ active: activeTab === 'faq' }" 
          @click="activeTab = 'faq'" 
          type="button"
        >
          <i class="fa-solid fa-circle-question me-2"></i>FAQ & Platform Info
        </button>
      </li>
    </ul>

    <!-- ── 3. TAB 1: CHANGELOGS ── -->
    <div v-show="activeTab === 'changelogs'" class="tab-pane fade show active">
      <!-- Clean Live Embed View Container -->
      <div class="card border-0 shadow-sm bg-white rounded-3 p-2 mb-4">
        <div class="embed-responsive-container rounded-3 overflow-hidden" style="min-height: 780px; background: #f8fafc;">
          <iframe 
            :srcdoc="embedHtmlDoc" 
            class="w-100 h-100 border-0" 
            style="min-height: 780px;"
            title="Spark Platform Guide Embed"
          ></iframe>
        </div>
      </div>
    </div>

    <!-- ── 4. TAB 2: MODULE GUIDES ── -->
    <div v-show="activeTab === 'modules'" class="tab-pane fade show active">
      <div class="row g-4">
        <div v-for="mod in platformModules" :key="mod.title" class="col-12 col-md-6 col-lg-4">
          <div class="card border-0 shadow-sm h-100 bg-white guide-card">
            <div class="card-body p-4 d-flex flex-column justify-content-between">
              <div>
                <div class="d-flex align-items-center gap-3 mb-3">
                  <div class="guide-icon-box" :style="{ backgroundColor: mod.bgColor, color: mod.color }">
                    <i :class="mod.icon"></i>
                  </div>
                  <div>
                    <h5 class="fw-bold mb-0 text-dark-blue">{{ mod.title }}</h5>
                    <span class="text-muted-custom text-xs">{{ mod.category }}</span>
                  </div>
                </div>
                <p class="text-muted-custom text-sm mb-3">{{ mod.description }}</p>
                <div class="features-list">
                  <h6 class="text-xs fw-bold text-secondary text-uppercase mb-2">Key Highlights</h6>
                  <ul class="list-unstyled mb-0 text-xs text-dark d-flex flex-column gap-1.5">
                    <li v-for="(f, fIdx) in mod.features" :key="fIdx" class="d-flex align-items-center gap-2">
                      <i class="fa-solid fa-circle-check text-info text-xxs"></i>
                      <span>{{ f }}</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="mt-4 pt-3 border-top border-light">
                <router-link :to="mod.route" class="btn btn-outline-info btn-sm w-100 fw-bold">
                  <span>Go to {{ mod.title }}</span>
                  <i class="fa-solid fa-arrow-right ms-1"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 5. TAB 3: FAQ & INFO ── -->
    <div v-show="activeTab === 'faq'" class="tab-pane fade show active">
      <div class="row justify-content-center">
        <div class="col-12 col-lg-9">
          <!-- Information Card -->
          <div class="card border-0 shadow-sm bg-white mb-4 p-4">
            <div class="d-flex align-items-center gap-3 mb-3">
              <div class="guide-icon-box" style="background-color: #e0f2fe; color: #0284c7;">
                <i class="fa-solid fa-rocket"></i>
              </div>
              <div>
                <h4 class="fw-bold mb-1 text-dark-blue">Release Cycles & Deployments</h4>
                <p class="text-muted-custom text-sm mb-0">How Spark platform changes are tested, tracked, and delivered.</p>
              </div>
            </div>
            <p class="text-dark text-sm mb-0">
              Spark follows a continuous delivery approach. Releases are validated across Dev and UAT regression suites before pushing to Production. Every deployment is recorded with version tags, clear summaries, and categorized feature notes in the Changelogs tab.
            </p>
          </div>

          <!-- FAQ Accordions -->
          <div class="card border-0 shadow-sm bg-white p-4">
            <h5 class="fw-bold text-dark-blue mb-3">Frequently Asked Questions</h5>
            <div class="accordion accordion-flush" id="faqAccordion">
              <div v-for="(faq, idx) in faqList" :key="idx" class="accordion-item border-bottom border-light">
                <h2 class="accordion-header" :id="'faqHeading' + idx">
                  <button 
                    class="accordion-button collapsed fw-bold text-dark text-sm py-3 px-0 bg-transparent" 
                    type="button" 
                    data-bs-toggle="collapse" 
                    :data-bs-target="'#faqCollapse' + idx" 
                    aria-expanded="false" 
                    :aria-controls="'faqCollapse' + idx"
                  >
                    <i class="fa-regular fa-circle-question text-info me-2"></i>
                    {{ faq.q }}
                  </button>
                </h2>
                <div 
                  :id="'faqCollapse' + idx" 
                  class="accordion-collapse collapse" 
                  :aria-labelledby="'faqHeading' + idx" 
                  data-bs-parent="#faqAccordion"
                >
                  <div class="accordion-body px-0 text-muted-custom text-sm pt-0 pb-3">
                    {{ faq.a }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 6. SUPERADMIN CREATE / EDIT RELEASE MODAL ── -->
    <div 
      class="modal fade" 
      id="releaseFormModal" 
      tabindex="-1" 
      aria-labelledby="releaseFormModalLabel" 
      aria-hidden="true"
      ref="releaseModal"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable custom-modal-scrollable" style="margin: 1.75rem auto;">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-white border-bottom sticky-top">
            <h5 class="modal-title fw-bold text-dark-blue" id="releaseFormModalLabel">
              <i class="fa-solid fa-newspaper text-info me-2"></i>
              {{ isEditing ? 'Edit Release Note' : 'Add New Release Note' }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body custom-modal-body p-4">
            <form @submit.prevent="saveRelease">
              <!-- Version & Title -->
              <div class="row g-3 mb-3">
                <div class="col-md-4">
                  <label class="form-label fw-bold text-secondary text-xs text-uppercase">Version Tag *</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="form.version" 
                    placeholder="e.g. v2.5.0" 
                    required 
                  />
                </div>
                <div class="col-md-8">
                  <label class="form-label fw-bold text-secondary text-xs text-uppercase">Release Title *</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="form.title" 
                    placeholder="e.g. Platform Guide & Release Transparency" 
                    required 
                  />
                </div>
              </div>

              <!-- Release Date & Type -->
              <div class="row g-3 mb-3">
                <div class="col-md-6">
                  <label class="form-label fw-bold text-secondary text-xs text-uppercase">Release Date *</label>
                  <input 
                    type="date" 
                    class="form-control" 
                    v-model="form.release_date" 
                    required 
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-bold text-secondary text-xs text-uppercase">Release Type *</label>
                  <select class="form-select" v-model="form.release_type">
                    <option value="major">Major Release</option>
                    <option value="minor">Minor Update</option>
                    <option value="patch">Patch</option>
                    <option value="hotfix">Hotfix</option>
                  </select>
                </div>
              </div>

              <!-- High Level Summary -->
              <div class="mb-4">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">High-Level Summary (User Friendly) *</label>
                <textarea 
                  class="form-control" 
                  rows="3" 
                  v-model="form.summary" 
                  placeholder="Explain clearly what this release introduces or fixes for the end user..." 
                  required
                ></textarea>
              </div>

              <!-- Dynamic Items Builder -->
              <div class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label fw-bold text-secondary text-xs text-uppercase mb-0">Categorized Changes & Fixes</label>
                  <button type="button" @click="addItem" class="btn btn-outline-info btn-xs fw-bold">
                    <i class="fa-solid fa-plus me-1"></i> Add Change Item
                  </button>
                </div>

                <div v-if="form.items.length === 0" class="text-center py-3 bg-light rounded-3 text-muted-custom text-xs">
                  No categorized items added. Click "+ Add Change Item" above.
                </div>

                <div v-else class="d-flex flex-column gap-2">
                  <div 
                    v-for="(item, idx) in form.items" 
                    :key="idx" 
                    class="border rounded-3 p-3 bg-light position-relative"
                  >
                    <button 
                      type="button" 
                      @click="removeItem(idx)" 
                      class="btn btn-link text-danger position-absolute top-0 end-0 p-2 text-decoration-none"
                      title="Remove Item"
                    >
                      <i class="fa-solid fa-xmark"></i>
                    </button>

                    <div class="row g-2 mb-2">
                      <div class="col-md-4">
                        <label class="form-label text-xxs text-secondary text-uppercase mb-1">Category</label>
                        <select class="form-select form-select-sm" v-model="item.category">
                          <option value="features">🚀 New Feature</option>
                          <option value="fixes">🛠️ Bug Fix</option>
                          <option value="improvements">⚡ Improvement</option>
                          <option value="security">🔒 Security</option>
                        </select>
                      </div>
                      <div class="col-md-5">
                        <label class="form-label text-xxs text-secondary text-uppercase mb-1">Item Title</label>
                        <input type="text" class="form-control form-control-sm" v-model="item.title" placeholder="e.g. Instant Search" />
                      </div>
                      <div class="col-md-3">
                        <label class="form-label text-xxs text-secondary text-uppercase mb-1">Badge / Module</label>
                        <input type="text" class="form-control form-control-sm" v-model="item.badge" placeholder="e.g. Contracts" />
                      </div>
                    </div>

                    <div>
                      <label class="form-label text-xxs text-secondary text-uppercase mb-1">Details (Optional)</label>
                      <input type="text" class="form-control form-control-sm" v-model="item.description" placeholder="Short description of the impact or behavior..." />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div class="modal-footer bg-light border-top">
            <button type="button" class="btn btn-outline-secondary btn-sm" data-bs-dismiss="modal">Cancel</button>
            <button 
              type="button" 
              class="btn btn-info text-white btn-sm fw-bold px-3" 
              @click="saveRelease" 
              :disabled="isSaving"
            >
              <span v-if="isSaving" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <i v-else class="fa-solid fa-floppy-disk me-1"></i>
              {{ isEditing ? 'Update Release' : 'Publish Release' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 7. DELETE CONFIRMATION MODAL ── -->
    <div 
      class="modal fade" 
      id="deleteReleaseModal" 
      tabindex="-1" 
      aria-hidden="true"
      ref="deleteModal"
    >
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content border-0 shadow text-center p-3">
          <div class="modal-body">
            <i class="fa-solid fa-trash-can text-danger fa-3x mb-3"></i>
            <h5 class="fw-bold text-dark mb-2">Delete Release Note?</h5>
            <p class="text-muted-custom text-xs mb-4">
              Are you sure you want to remove release <strong>{{ selectedRelease?.version }}</strong>? This action will archive the record.
            </p>
            <div class="d-flex justify-content-center gap-2">
              <button type="button" class="btn btn-outline-secondary btn-sm" data-bs-dismiss="modal">Cancel</button>
              <button 
                type="button" 
                class="btn btn-danger btn-sm fw-bold px-3" 
                @click="deleteRelease" 
                :disabled="isDeleting"
              >
                <span v-if="isDeleting" class="spinner-border spinner-border-sm me-1"></span>
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal as BootstrapModal } from 'bootstrap';

export default {
  name: "PlatformGuideDashboard",
  data() {
    return {
      viewMode: "iframe", // "iframe" | "feed"
      embedHtmlContent: "",
      isEmbedLoading: false,
      activeTab: "changelogs",
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      changelogs: [],

      // Filters
      searchQuery: "",
      selectedCategory: "",
      selectedReleaseType: "",
      categoryFilters: [
        { label: "All", value: "", icon: "fa-solid fa-list-ul" },
        { label: "Features", value: "features", icon: "fa-solid fa-rocket" },
        { label: "Bug Fixes", value: "fixes", icon: "fa-solid fa-wrench" },
        { label: "Improvements", value: "improvements", icon: "fa-solid fa-bolt" },
        { label: "Security", value: "security", icon: "fa-solid fa-shield-halved" },
      ],

      // Form Modal State
      isEditing: false,
      selectedRelease: null,
      form: {
        id: null,
        version: "",
        title: "",
        summary: "",
        release_date: new Date().toISOString().split("T")[0],
        release_type: "minor",
        is_published: true,
        items: [],
      },

      // Static Module Guides
      platformModules: [
        {
          title: "Sales Pipeline & Leads",
          category: "CRM & Opportunities",
          icon: "fa-solid fa-users-line",
          bgColor: "#eef2ff",
          color: "#4f46e5",
          route: "/leads",
          description: "Manage incoming leads, qualification workflows, and seamless conversion into client proposals.",
          features: ["Lead qualification & notes", "Document & file attachments", "One-click proposal conversion"],
        },
        {
          title: "Proposals Engine",
          category: "Commercial Proposals",
          icon: "fa-solid fa-file-contract",
          bgColor: "#ecfdf5",
          color: "#059669",
          route: "/proposals",
          description: "Create interactive client-facing proposals with dynamic pricing tables and instant client feedback.",
          features: ["WYSIWYG proposal editor", "Client questions & renewal workflows", "Automated expiry reminders"],
        },
        {
          title: "Contracts & E-Signing",
          category: "Legal & Execution",
          icon: "fa-solid fa-file-signature",
          bgColor: "#fef3c7",
          color: "#d97706",
          route: "/contracts",
          description: "End-to-end contract negotiation with inline clause revision threads and electronic signature handoffs.",
          features: ["Xodo Sign integration", "Threaded revision discussions", "Multi-party signer workflows"],
        },
        {
          title: "Regression Library",
          category: "Quality Assurance",
          icon: "fa-solid fa-list-check",
          bgColor: "#f0fdf4",
          color: "#16a34a",
          route: "/regression-library",
          description: "Track test cases, sprint regression runs, and automated audit trails across platform releases.",
          features: ["Sprint test management", "Audit trail tracking", "CSV import/export"],
        },
        {
          title: "Platform Quick Links",
          category: "Platforms Delivery",
          icon: "fa-solid fa-link",
          bgColor: "#f0f9ff",
          color: "#0284c7",
          route: "/quick-links",
          description: "Central directory connecting teams to Dev, UAT, and Production environments and Jira platform boards.",
          features: ["Categorized hub shortcuts", "Instant search filtering", "Customizable directory links"],
        },
      ],

      // FAQ List
      faqList: [
        {
          q: "How often are updates deployed to production?",
          a: "Spark platform features and bug fixes are deployed in bi-weekly release trains, with critical hotfixes deployed immediately upon review and regression test verification.",
        },
        {
          q: "Where do I report an issue or suggest a feature?",
          a: "You can file issues directly in the Jira Platform Boards accessible via the Platforms Delivery > Quick Links menu, or contact your platform administrator.",
        },
        {
          q: "Are previous version changelogs preserved?",
          a: "Yes! All historical releases are saved in the platform database and remain searchable anytime via the Changelogs tab.",
        },
      ],
    };
  },

  computed: {
    latestVersion() {
      if (this.changelogs && this.changelogs.length > 0) {
        return this.changelogs[0].version;
      }
      return null;
    },

    apiBaseUrl() {
      let base = (this.$axios && this.$axios.defaults && this.$axios.defaults.baseURL) 
        ? this.$axios.defaults.baseURL 
        : (process.env.VUE_APP_API_BASE_URL || window.location.origin);
      if (base.endsWith("/")) {
        base = base.slice(0, -1);
      }
      return base;
    },

    embedHtmlDoc() {
      if (this.embedHtmlContent) {
        return this.embedHtmlContent;
      }
      return this.generateEmbedHtml(this.changelogs);
    },
    filteredChangelogs() {
      return this.changelogs.filter((release) => {
        // Search query match
        if (this.searchQuery.trim()) {
          const q = this.searchQuery.toLowerCase();
          const matchVersion = (release.version || "").toLowerCase().includes(q);
          const matchTitle = (release.title || "").toLowerCase().includes(q);
          const matchSummary = (release.summary || "").toLowerCase().includes(q);
          const matchItems = (release.items || []).some(
            (i) =>
              (i.title || "").toLowerCase().includes(q) ||
              (i.description || "").toLowerCase().includes(q) ||
              (i.badge || "").toLowerCase().includes(q)
          );
          if (!matchVersion && !matchTitle && !matchSummary && !matchItems) {
            return false;
          }
        }

        // Release type filter
        if (this.selectedReleaseType && release.release_type !== this.selectedReleaseType) {
          return false;
        }

        // Category filter
        if (this.selectedCategory) {
          const hasCategory = (release.items || []).some(
            (i) => i.category === this.selectedCategory
          );
          if (!hasCategory) return false;
        }

        return true;
      });
    },
  },

  mounted() {
    this.fetchChangelogs();
    this.fetchEmbedPreview();
  },

  methods: {
    fetchEmbedPreview() {
      this.isEmbedLoading = true;
      this.$axios
        .get("/api/public/changelogs/spark/embed")
        .then((res) => {
          this.embedHtmlContent = typeof res.data === "string" ? res.data : "";
        })
        .catch((err) => {
          console.error("Error loading embed preview in Platform Guide:", err);
          this.embedHtmlContent = this.generateEmbedHtml(this.changelogs);
        })
        .finally(() => {
          this.isEmbedLoading = false;
        });
    },

        generateEmbedHtml(logs) {
      const pName = "Spark Core";
      const pColor = "#00a0c1";
      const logoHtml = `<div style="display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 8px; background: rgba(0, 160, 193, 0.1); border: 1px solid #e2e8f0; flex-shrink: 0;"><i class="fa-solid fa-layer-group" style="font-size: 18px; color: ${pColor};"></i></div>`;
      let bodyContent = "";

      if (!logs || logs.length === 0) {
        bodyContent = `<div class="card" style="text-align: center; color: #64748b; padding: 40px; background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0;">No release notes published yet.</div>`;
      } else {
        logs.forEach((c) => {
          let itemsHtml = "";
          let catsList = [];
          let fullSearchText = (c.version || "") + " " + (c.title || "") + " " + (c.summary || "") + " ";

          const cats = [
            { key: "features", label: "New Features", icon: "🚀", color: "#16a34a", bg: "rgba(34, 197, 94, 0.08)" },
            { key: "improvements", label: "Improvements", icon: "⚡", color: "#0284c7", bg: "rgba(14, 165, 233, 0.08)" },
            { key: "fixes", label: "Bug Fixes", icon: "🛠️", color: "#d97706", bg: "rgba(245, 158, 11, 0.08)" },
            { key: "security", label: "Security", icon: "🔒", color: "#8b5cf6", bg: "rgba(139, 92, 246, 0.08)" },
          ];

          cats.forEach((cat) => {
            const catItems = (c.items || []).filter((i) => i.category === cat.key);
            if (catItems.length > 0) {
              catsList.push(cat.key);
              itemsHtml += `
                <div class="cat-block" data-cat-key="${cat.key}" style="margin-top: 14px; background: ${cat.bg}; border-left: 3px solid ${cat.color}; padding: 12px 14px; border-radius: 6px;">
                  <div style="display: flex; align-items: center; gap: 6px; font-weight: 700; font-size: 11px; text-transform: uppercase; color: ${cat.color}; margin-bottom: 6px; letter-spacing: 0.5px;">
                    <span>${cat.icon}</span> <span>${cat.label}</span>
                  </div>
                  <ul style="margin: 0; padding-left: 20px; font-size: 13px; color: #334155; line-height: 1.6;">
                    ${catItems
                      .map((i) => {
                        fullSearchText += (i.title || "") + " " + (i.description || "") + " " + (i.badge || "") + " ";
                        return `<li><strong>${i.title}</strong>${i.badge ? ` <span style="background: #ffffff; border: 1px solid #cbd5e1; font-size: 10px; padding: 2px 6px; border-radius: 4px; color: #475569; font-weight: 600;">${i.badge}</span>` : ""}${i.description ? ` <span style="color: #64748b;">— ${i.description}</span>` : ""}</li>`;
                      })
                      .join("")}
                  </ul>
                </div>
              `;
            }
          });

          const formattedDate = c.release_date ? new Date(c.release_date).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
          const typeColor = c.release_type === 'major' ? '#ef4444' : c.release_type === 'minor' ? '#0284c7' : '#10b981';

          bodyContent += `
            <div class="card" data-version="${c.version || ''}" data-type="${c.release_type || 'minor'}" data-categories="${catsList.join(',')}" data-search="${fullSearchText.toLowerCase()}" style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.04);">
              <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #f1f5f9;">
                <div style="display: flex; align-items: center; gap: 14px;">
                  <span style="background: ${pColor}; color: #ffffff; padding: 5px 14px; border-radius: 20px; font-weight: 800; font-size: 13.5px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; letter-spacing: 0.5px; display: inline-flex; align-items: center; gap: 8px; box-shadow: 0 2px 6px rgba(0,0,0,0.12);">
                    🏷️ <span>${c.version || 'v1.0.0'}</span>
                  </span>
                  <h3 style="margin: 0; font-size: 16px; font-weight: 700; color: #0f172a;">${c.title || 'Platform Release'}</h3>
                </div>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="font-size: 12px; color: #64748b;">📅 ${formattedDate}</span>
                  <span style="padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; text-transform: uppercase; background: ${typeColor}15; color: ${typeColor};">
                    ${c.release_type || 'minor'}
                  </span>
                </div>
              </div>
              ${c.summary ? `<div style="padding: 10px 14px; border-radius: 8px; background: #f8fafc; border-left: 3px solid ${pColor}; margin-bottom: 14px; font-size: 13px; color: #475569; line-height: 1.5;">${c.summary}</div>` : ''}
              ${itemsHtml}
            </div>
          `;
        });
      }

      return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${pName} Releases</title>
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      background-color: #f8fafc;
      color: #0f172a;
      margin: 0;
      padding: 20px;
      line-height: 1.5;
    }
    .container { max-width: 860px; margin: 0 auto; }
    .embed-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 2px solid #e2e8f0;
    }
    .embed-title {
      font-size: 20px;
      font-weight: 800;
      color: #0f172a;
    }

    /* Filter Toolbar */
    .filter-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;
      margin-bottom: 20px;
    }
    .search-wrap {
      position: relative;
      flex: 1 1 240px;
      max-width: 360px;
    }
    .search-input {
      width: 100%;
      padding: 8px 32px 8px 34px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      background: #ffffff;
      color: #0f172a;
      font-size: 13.5px;
      outline: none;
      transition: all 0.2s ease;
    }
    .search-input:focus {
      border-color: #00a0c1;
      box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
    }
    .search-icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: #64748b;
      pointer-events: none;
    }
    .clear-btn {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: #64748b;
      cursor: pointer;
      font-size: 16px;
      line-height: 1;
      padding: 2px;
    }
    .pills-wrap {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      align-items: center;
    }
    .cat-pill {
      padding: 5px 12px;
      border-radius: 20px;
      border: 1px solid #e2e8f0;
      background: #ffffff;
      color: #64748b;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    .cat-pill:hover {
      background: #f8fafc;
      color: #0f172a;
      border-color: #00a0c1;
    }
    .cat-pill.active {
      background: #00a0c1;
      color: #ffffff;
      border-color: #00a0c1;
      font-weight: 700;
    }
    .release-count {
      font-size: 12px;
      color: #64748b;
      font-weight: 600;
    }
  </style>
</head>
<body>
<div class="container">
  <div class="embed-header">
    <div class="embed-title" style="display: flex; align-items: center; gap: 12px;">
      ${logoHtml}
      <span>${pName} Changelogs</span>
    </div>
    <div class="powered-badge" style="display: inline-flex; align-items: center; gap: 7px; font-size: 11px; font-weight: 700; color: #64748b; background: #ffffff; border: 1px solid #e2e8f0; padding: 4px 12px; border-radius: 20px; box-shadow: 0 1px 2px rgba(0,0,0,0.03);">
      <span style="text-transform: uppercase; letter-spacing: 0.5px; font-size: 10px; opacity: 0.85;">Powered by</span>
      <div style="display: inline-flex; align-items: center; gap: 5px;">
        <svg width="12" height="15" viewBox="0 0 65 90" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: block;">
          <path d="M16.8892 89.1421L52.1768 59.7479C64.4804 44.2444 51.2058 33.2613 51.2058 33.2613C44.0857 25.8332 33.0766 29.3828 33.0766 29.3828L30.1638 39.3949L42.7911 42.9498L24.3382 59.7479L16.8944 89.1421H16.8892Z" fill="#A3C0D1"/>
          <path d="M40.5151 0L5.22745 29.3942C-7.07623 44.8977 6.19838 55.8807 6.19838 55.8807C13.3185 63.3089 24.3276 59.7592 24.3276 59.7592L27.2404 49.7472L14.6131 46.1923L33.0712 29.3942L40.5151 0Z" fill="#00A0C1"/>
        </svg>
        <span style="font-weight: 900; font-size: 12px; color: #1e293b; letter-spacing: 0.8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">SPARK</span>
      </div>
    </div>
  </div>

  <!-- Interactive Filter Toolbar -->
  <div class="filter-bar">
    <div class="search-wrap">
      <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
      <input type="text" id="embedSearch" class="search-input" placeholder="Search features, fixes, version..." oninput="filterReleases()" />
      <button type="button" id="clearSearchBtn" class="clear-btn" onclick="clearSearch()" style="display: none;">&times;</button>
    </div>
    <div class="pills-wrap">
      <button type="button" class="cat-pill active" onclick="setCategory('all', this)">All</button>
      <button type="button" class="cat-pill" onclick="setCategory('features', this)">🚀 Features</button>
      <button type="button" class="cat-pill" onclick="setCategory('fixes', this)">🛠️ Bug Fixes</button>
      <button type="button" class="cat-pill" onclick="setCategory('improvements', this)">⚡ Improvements</button>
      <button type="button" class="cat-pill" onclick="setCategory('security', this)">🔒 Security</button>
    </div>
    <div class="release-count" id="releaseCount">${(logs || []).length} releases</div>
  </div>

  ${bodyContent}
  <div id="noResultsMsg" class="card" style="display: none; text-align: center; color: #64748b; padding: 40px; background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0;">No release notes matching your search or filters.</div>
</div>

<script>
  var activeCategory = 'all';

  function filterReleases() {
    var searchInput = document.getElementById('embedSearch');
    var clearBtn = document.getElementById('clearSearchBtn');
    var query = (searchInput ? searchInput.value : '').toLowerCase().trim();
    if (clearBtn) {
      clearBtn.style.display = query ? 'block' : 'none';
    }

    var cards = document.querySelectorAll('.card[data-version]');
    var count = 0;

    cards.forEach(function(card) {
      var searchData = (card.getAttribute('data-search') || '').toLowerCase();
      var cats = (card.getAttribute('data-categories') || '').split(',');
      var matchQuery = !query || searchData.indexOf(query) !== -1;
      var matchCat = activeCategory === 'all' || cats.indexOf(activeCategory) !== -1;

      if (matchQuery && matchCat) {
        card.style.display = 'block';
        count++;
        var blocks = card.querySelectorAll('.cat-block');
        blocks.forEach(function(b) {
          if (activeCategory === 'all') {
            b.style.display = 'block';
          } else {
            var catKey = b.getAttribute('data-cat-key');
            b.style.display = (catKey === activeCategory) ? 'block' : 'none';
          }
        });
      } else {
        card.style.display = 'none';
      }
    });

    var noRes = document.getElementById('noResultsMsg');
    if (noRes) {
      noRes.style.display = (count === 0) ? 'block' : 'none';
    }

    var countEl = document.getElementById('releaseCount');
    if (countEl) {
      countEl.textContent = count + (count === 1 ? ' release' : ' releases');
    }
  }

  function setCategory(cat, el) {
    activeCategory = cat;
    var pills = document.querySelectorAll('.cat-pill');
    pills.forEach(function(p) { p.classList.remove('active'); });
    if (el) el.classList.add('active');
    filterReleases();
  }

  function clearSearch() {
    var searchInput = document.getElementById('embedSearch');
    if (searchInput) {
      searchInput.value = '';
    }
    filterReleases();
  }
${'<'}/script>
</body>
</html>`;
    },
    openModalSafe(modalId) {
      const modalEl = document.getElementById(modalId);
      if (modalEl) {
        try {
          const bootstrapObj = (window.bootstrap && window.bootstrap.Modal) ? window.bootstrap : { Modal: BootstrapModal };
          if (bootstrapObj && bootstrapObj.Modal) {
            const modalInstance = bootstrapObj.Modal.getInstance(modalEl) || new bootstrapObj.Modal(modalEl);
            modalInstance.show();
          }
        } catch (e) {
          console.error("Error opening modal programmatically:", e);
        }
      }
    },

    closeModalSafe(modalId) {
      const modalEl = document.getElementById(modalId);
      if (modalEl) {
        const closeBtn = modalEl.querySelector('[data-bs-dismiss="modal"]');
        if (closeBtn) {
          closeBtn.click();
        } else {
          try {
            const bootstrapObj = (window.bootstrap && window.bootstrap.Modal) ? window.bootstrap : { Modal: BootstrapModal };
            if (bootstrapObj && bootstrapObj.Modal) {
              const modalInstance = bootstrapObj.Modal.getInstance(modalEl) || new bootstrapObj.Modal(modalEl);
              modalInstance.hide();
            }
          } catch (e) {
            console.error("Error closing modal programmatically:", e);
          }
        }
      }
      setTimeout(() => {
        const backdrops = document.querySelectorAll(".modal-backdrop");
        backdrops.forEach((el) => el.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }, 150);
    },
    fetchChangelogs() {
      this.isLoading = true;
      this.$axios
        .get("/api/platform-guide/changelogs")
        .then((response) => {
          if (response.data && response.data.success) {
            this.changelogs = response.data.data || [];
            this.fetchEmbedPreview();
          }
        })
        .catch((err) => {
          console.error("Failed to load changelogs:", err);
          if (this.toast) {
            this.toast.error("Could not load platform changelogs.");
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    resetFilters() {
      this.searchQuery = "";
      this.selectedCategory = "";
      this.selectedReleaseType = "";
    },

    getItemsByCategory(items, category) {
      if (!items) return [];
      return items.filter((i) => i.category === category);
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-AU", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    formatReleaseType(type) {
      switch (type) {
        case "major":
          return "Major Release";
        case "minor":
          return "Minor Update";
        case "patch":
          return "Patch";
        case "hotfix":
          return "Hotfix";
        default:
          return "Release";
      }
    },

    getVersionBadgeClass(release) {
      switch (release.release_type) {
        case "major":
          return "bg-primary";
        case "minor":
          return "bg-info";
        case "hotfix":
          return "bg-danger";
        case "patch":
          return "bg-warning text-dark";
        default:
          return "bg-secondary";
      }
    },

    getReleaseTypeClass(type) {
      switch (type) {
        case "major":
          return "bg-primary-subtle text-primary border border-primary-subtle";
        case "minor":
          return "bg-info-subtle text-info-dark border border-info-subtle";
        case "hotfix":
          return "bg-danger-subtle text-danger border border-danger-subtle";
        case "patch":
          return "bg-warning-subtle text-warning-dark border border-warning-subtle";
        default:
          return "bg-light text-secondary border";
      }
    },

    openAddReleaseModal() {
      this.isEditing = false;
      this.selectedRelease = null;
      this.form = {
        id: null,
        version: "",
        title: "",
        summary: "",
        release_date: new Date().toISOString().split("T")[0],
        release_type: "minor",
        is_published: true,
        items: [
          { category: "features", title: "", description: "", badge: "" },
        ],
      };
      const modalEl = document.getElementById("releaseFormModal");
      if (modalEl && window.bootstrap) {
        const modal = new window.bootstrap.Modal(modalEl);
        modal.show();
      }
    },

    openEditReleaseModal(release) {
      this.isEditing = true;
      this.selectedRelease = release;
      this.form = {
        id: release.id,
        version: release.version,
        title: release.title,
        summary: release.summary,
        release_date: release.release_date
          ? new Date(release.release_date).toISOString().split("T")[0]
          : "",
        release_type: release.release_type || "minor",
        is_published: release.is_published,
        items: release.items ? JSON.parse(JSON.stringify(release.items)) : [],
      };
      const modalEl = document.getElementById("releaseFormModal");
      if (modalEl && window.bootstrap) {
        const modal = new window.bootstrap.Modal(modalEl);
        modal.show();
      }
    },

    addItem() {
      this.form.items.push({
        category: "features",
        title: "",
        description: "",
        badge: "",
      });
    },

    removeItem(idx) {
      this.form.items.splice(idx, 1);
    },

    saveRelease() {
      if (!this.form.version.trim() || !this.form.title.trim() || !this.form.summary.trim()) {
        if (this.toast) this.toast.error("Please fill in version, title, and summary.");
        return;
      }

      this.isSaving = true;
      const payload = {
        version: this.form.version,
        title: this.form.title,
        summary: this.form.summary,
        release_date: this.form.release_date,
        release_type: this.form.release_type,
        is_published: this.form.is_published,
        items: this.form.items.filter((i) => i.title.trim()),
      };

      const request = this.isEditing
        ? this.$axios.put(`/api/platform-guide/changelogs/${this.form.id}`, payload)
        : this.$axios.post("/api/platform-guide/changelogs", payload);

      request
        .then((response) => {
          if (response.data && response.data.success) {
            if (this.toast) {
              this.toast.success(
                this.isEditing ? "Release note updated!" : "Release note published!"
              );
            }
            this.fetchChangelogs();
            const modalEl = document.getElementById("releaseFormModal");
            if (modalEl && window.bootstrap) {
              const modal = window.bootstrap.Modal.getInstance(modalEl);
              if (modal) modal.hide();
            }
          } else {
            if (this.toast) this.toast.error(response.data.message || "Failed to save release.");
          }
        })
        .catch((err) => {
          console.error("Error saving release:", err);
          if (this.toast) this.toast.error("An error occurred while saving.");
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    confirmDeleteRelease(release) {
      this.selectedRelease = release;
      const modalEl = document.getElementById("deleteReleaseModal");
      if (modalEl && window.bootstrap) {
        const modal = new window.bootstrap.Modal(modalEl);
        modal.show();
      }
    },

    deleteRelease() {
      if (!this.selectedRelease) return;
      this.isDeleting = true;
      this.$axios
        .delete(`/api/platform-guide/changelogs/${this.selectedRelease.id}`)
        .then((response) => {
          if (response.data && response.data.success) {
            if (this.toast) this.toast.success("Release note deleted.");
            this.fetchChangelogs();
            const modalEl = document.getElementById("deleteReleaseModal");
            if (modalEl && window.bootstrap) {
              const modal = window.bootstrap.Modal.getInstance(modalEl);
              if (modal) modal.hide();
            }
          } else {
            if (this.toast) this.toast.error(response.data.message || "Failed to delete.");
          }
        })
        .catch((err) => {
          console.error("Error deleting release:", err);
          if (this.toast) this.toast.error("Failed to delete release.");
        })
        .finally(() => {
          this.isDeleting = false;
        });
    },
  },
};
</script>

<style scoped>
.platform-guide-page {
  background-color: transparent;
}

.text-dark-blue {
  color: #1e293b;
}

.text-muted-custom {
  color: #64748b;
}

.text-xxs {
  font-size: 11px;
}

.text-xs {
  font-size: 12px;
}

.text-sm {
  font-size: 13.5px;
}

/* ── Top-Level Tabs Custom Styling (Matching Debugger) ── */
.nav-tabs {
  border-bottom: 2px solid #e9ecef !important;
}
.nav-tabs .nav-link {
  border: none !important;
  color: #6c757d !important;
  background: transparent !important;
  transition: all 0.2s ease;
  position: relative;
  padding: 12px 24px !important;
}
.nav-tabs .nav-link:hover {
  color: #00a0c1 !important;
}
.nav-tabs .nav-link.active {
  color: #00a0c1 !important;
  background: transparent !important;
}
.nav-tabs .nav-link.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #00a0c1;
  border-radius: 3px;
}

/* Filter Toolbar Card & Segmented Controls */
.filter-toolbar-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.category-pills-group {
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.category-pills-group .btn {
  font-size: 12px;
  border: none;
  transition: all 0.15s ease-in-out;
}

.category-pills-group .btn.active-filter {
  background-color: #00a0c1 !important;
  color: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 160, 193, 0.25);
}

.category-pills-group .btn:not(.active-filter):hover {
  background-color: #e2e8f0 !important;
  color: #1e293b !important;
}

/* Changelog Timeline & Cards */
.changelog-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.changelog-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05) !important;
}

.version-badge {
  font-size: 13px;
  letter-spacing: 0.5px;
}

.summary-banner {
  background: #f8fafc;
  border-left: 3px solid #00a0c1;
}

/* Change Categories */
.bg-features-subtle {
  background-color: #f0fdf4;
}

.bg-fixes-subtle {
  background-color: #fffbeb;
}

.bg-improvements-subtle {
  background-color: #f0f9ff;
}

.bg-security-subtle {
  background-color: #f5f3ff;
}

.text-warning-dark {
  color: #b45309;
}

.text-info-dark {
  color: #0369a1;
}

/* Guide Module Cards */
.guide-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.guide-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.06) !important;
}

.guide-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

/* Search Glow */
.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
}

.focus-glow:focus {
  background: #ffffff !important;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
  border-color: #00a0c1 !important;
}

.shadow-xs {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* ── Custom Styled Action Buttons ── */
.btn-cyan-primary {
  background-color: #00a0c1 !important;
  color: #ffffff !important;
  border: 1px solid #00a0c1 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease-in-out !important;
  box-shadow: 0 2px 4px rgba(0, 160, 193, 0.2) !important;
}

.btn-cyan-primary:hover {
  background-color: #008ca9 !important;
  border-color: #008ca9 !important;
  color: #ffffff !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 160, 193, 0.3) !important;
}

.btn-cyan-outline {
  background-color: #ffffff !important;
  color: #00a0c1 !important;
  border: 1.5px solid #00a0c1 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease-in-out !important;
}

.btn-cyan-outline:hover {
  background-color: #e6f7fa !important;
  color: #008ca9 !important;
  border-color: #008ca9 !important;
  transform: translateY(-1px);
}

.delete-btn {
  transition: all 0.2s ease;
}

.delete-btn:hover {
  background-color: #dc3545 !important;
  color: #ffffff !important;
  border-color: #dc3545 !important;
}


/* ── Header Secondary Button & Outline Fix ── */
.btn-header-secondary {
  background-color: #ffffff !important;
  color: #334155 !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
}

.btn-header-secondary:hover,
.btn-header-secondary:focus,
.btn-header-secondary:active {
  background-color: #f1f5f9 !important;
  color: #0f172a !important;
  border-color: #94a3b8 !important;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.06) !important;
}

.btn-header-secondary:hover i,
.btn-header-secondary:focus i,
.btn-header-secondary:active i {
  color: #008ca9 !important;
}

.btn-header-secondary:hover span,
.btn-header-secondary:focus span,
.btn-header-secondary:active span {
  color: #0f172a !important;
}

.btn-outline-secondary {
  background-color: #ffffff !important;
  color: #475569 !important;
  border: 1px solid #cbd5e1 !important;
  transition: all 0.15s ease-in-out !important;
}

.btn-outline-secondary:hover,
.btn-outline-secondary:focus,
.btn-outline-secondary:active {
  background-color: #f1f5f9 !important;
  color: #0f172a !important;
  border-color: #94a3b8 !important;
}

.btn-outline-secondary:hover i,
.btn-outline-secondary:focus i,
.btn-outline-secondary:active i {
  color: #008ca9 !important;
}

.btn-outline-secondary:hover span,
.btn-outline-secondary:focus span,
.btn-outline-secondary:active span {
  color: #0f172a !important;
}

/* Custom Scrollable Modals - Guaranteed Visible Header & Footer */
.custom-modal-scrollable {
  max-height: calc(100vh - 2rem) !important;
  display: flex !important;
  flex-direction: column !important;
}

.custom-modal-scrollable .modal-content {
  max-height: calc(100vh - 2.5rem) !important;
  display: flex !important;
  flex-direction: column !important;
  border-radius: 12px;
  overflow: hidden;
}

.custom-modal-scrollable .modal-header {
  flex-shrink: 0;
  background-color: #ffffff;
  z-index: 1055;
  border-bottom: 1px solid #e2e8f0;
}

.custom-modal-scrollable .custom-modal-body {
  flex: 1 1 auto;
  overflow-y: auto !important;
  max-height: calc(100vh - 160px) !important;
  padding: 1.25rem 1.5rem !important;
}

.custom-modal-scrollable .modal-footer {
  flex-shrink: 0;
  background-color: #f8fafc;
  z-index: 1055;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.03);
}

.version-pill-highlight-sm {
  background: linear-gradient(135deg, #00a0c1 0%, #00738c 100%) !important;
  color: #ffffff !important;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
  font-size: 11.5px !important;
  font-weight: 800 !important;
  padding: 4px 12px !important;
  border-radius: 12px !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 1px 4px rgba(0, 160, 193, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  line-height: 1.4 !important;
  vertical-align: middle !important;
}

</style>
